/**
 * Upload records that survive a reload, so an interrupted upload can resume
 * when the user selects the same file again. Only identifiers are stored,
 * never file contents.
 */
export interface PersistedUpload {
  uploadId: string;
  assetId: string;
  title: string;
  name: string;
  size: number;
  lastModified: number;
  drm: boolean;
  startedAt: number;
}

const KEY = "wv-uploads";
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // upload sessions expire after 24 h

export function loadPersisted(now = Date.now()): PersistedUpload[] {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? "[]") as PersistedUpload[];
    return Array.isArray(raw) ? raw.filter((u) => now - u.startedAt < MAX_AGE_MS) : [];
  } catch {
    return [];
  }
}

function save(records: PersistedUpload[]): void {
  try {
    if (records.length) localStorage.setItem(KEY, JSON.stringify(records));
    else localStorage.removeItem(KEY);
  } catch {
    // Storage unavailable: uploads still work, they just can't resume after a reload.
  }
}

export function persist(record: PersistedUpload): void {
  save([...loadPersisted().filter((r) => r.uploadId !== record.uploadId), record]);
}

export function forget(uploadId: string): void {
  save(loadPersisted().filter((r) => r.uploadId !== uploadId));
}

/** The persisted upload this file belongs to, matched by name, size and date. */
export function matchFile(records: PersistedUpload[], file: File): PersistedUpload | undefined {
  return records.find(
    (r) => r.name === file.name && r.size === file.size && r.lastModified === file.lastModified,
  );
}
